import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon, PhoneIcon, ChevronDownIcon } from 'lucide-react';
export function Navbar({
  onNavigate,
  currentPage
}: {
  onNavigate: (page: string) => void;
  currentPage: string;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNav = (page: string) => {
    onNavigate(page);
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  };
  const linkClass = (page?: string) => `font-medium transition-colors cursor-pointer ${page && currentPage === page ? 'text-primary' : 'text-slate-600 hover:text-primary'}`;
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-3' : 'bg-white py-5'}`} data-id="element-129">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-130">
        <div className="flex justify-between items-center" data-id="element-131">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center" data-id="element-132">
            <button onClick={() => handleNav('home')} className="block" data-id="element-133">
              <img src="https://cdn.magicpatterns.com/uploads/pEd3egThTWpVgMyyVxGfbc/ambient-telecom-logoo.png" alt="Ambient Telecom Solutions" className="h-10 w-auto" data-id="element-134" />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8 items-center" data-id="element-135">
            <button onClick={() => handleNav('about')} className={linkClass('about')} data-id="element-136">
              About Us
            </button>

            {/* Services Dropdown */}
            <div className="relative group" onMouseEnter={() => setServicesDropdownOpen(true)} onMouseLeave={() => setServicesDropdownOpen(false)} data-id="element-137">
              <button className={`flex items-center hover:text-primary font-medium transition-colors py-2 ${['business-internet', 'ethernet-fiber', 'network-security', 'ucaas', 'business-phone'].includes(currentPage) ? 'text-primary' : 'text-slate-600'}`} data-id="element-138">
                Services <ChevronDownIcon className="ml-1 w-4 h-4" data-id="element-139" />
              </button>

              {servicesDropdownOpen && <div className="absolute top-full left-0 w-56 bg-white shadow-xl rounded-lg py-2 border border-slate-100 transition-opacity" data-id="element-140">
                  <button onClick={() => handleNav('business-internet')} className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-50 hover:text-primary ${currentPage === 'business-internet' ? 'text-primary font-medium bg-slate-50' : 'text-slate-700'}`} data-id="element-141">
                    Business Internet
                  </button>
                  <button onClick={() => handleNav('ethernet-fiber')} className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-50 hover:text-primary ${currentPage === 'ethernet-fiber' ? 'text-primary font-medium bg-slate-50' : 'text-slate-700'}`} data-id="element-142">
                    Ethernet-Fiber
                  </button>
                  <button onClick={() => handleNav('network-security')} className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-50 hover:text-primary ${currentPage === 'network-security' ? 'text-primary font-medium bg-slate-50' : 'text-slate-700'}`} data-id="element-143">
                    Network Security
                  </button>
                  <button onClick={() => handleNav('ucaas')} className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-50 hover:text-primary ${currentPage === 'ucaas' ? 'text-primary font-medium bg-slate-50' : 'text-slate-700'}`} data-id="element-144">
                    UCaaS
                  </button>
                  <button onClick={() => handleNav('business-phone')} className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-50 hover:text-primary ${currentPage === 'business-phone' ? 'text-primary font-medium bg-slate-50' : 'text-slate-700'}`} data-id="element-145">
                    Business Phone
                  </button>
                </div>}
            </div>

            <button onClick={() => handleNav('suppliers')} className={linkClass('suppliers')} data-id="element-146">
              Suppliers
            </button>
            <button onClick={() => handleNav('contact')} className={linkClass('contact')} data-id="element-147">
              Contact Us
            </button>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-6" data-id="element-148">
            <a href="tel:+4105255284" className="flex items-center text-slate-700 hover:text-primary font-medium transition-colors" data-id="element-149">
              <PhoneIcon className="w-4 h-4 mr-2 text-primary" data-id="element-150" />
              +410-525-5284
            </a>
            <button onClick={() => handleNav('home')} className="bg-primary hover:bg-primary-dark text-white px-6 py-2.5 rounded-full font-medium transition-colors shadow-sm" data-id="element-151">
              Get a Quote
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center" data-id="element-152">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-slate-600 hover:text-primary focus:outline-none" aria-label="Toggle menu" data-id="element-153">
              {mobileMenuOpen ? <XIcon className="h-6 w-6" data-id="element-154" /> : <MenuIcon className="h-6 w-6" data-id="element-155" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && <div className="lg:hidden bg-white border-t border-slate-100 absolute top-full left-0 w-full shadow-lg" data-id="element-156">
          <div className="px-4 pt-2 pb-6 space-y-1" data-id="element-157">
            <button onClick={() => handleNav('about')} className={`block w-full text-left px-3 py-3 text-base font-medium rounded-md ${currentPage === 'about' ? 'text-primary bg-primary/5' : 'text-slate-700 hover:bg-slate-50 hover:text-primary'}`} data-id="element-158">
              About Us
            </button>

            <div className="px-3 py-2" data-id="element-159">
              <div className="text-base font-medium text-slate-900 mb-2" data-id="element-160">
                Services
              </div>
              <div className="pl-4 space-y-2 border-l-2 border-slate-100" data-id="element-161">
                <button onClick={() => handleNav('business-internet')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'business-internet' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-162">
                  Business Internet
                </button>
                <button onClick={() => handleNav('ethernet-fiber')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'ethernet-fiber' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-163">
                  Ethernet-Fiber
                </button>
                <button onClick={() => handleNav('network-security')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'network-security' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-164">
                  Network Security
                </button>
                <button onClick={() => handleNav('ucaas')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'ucaas' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-165">
                  UCaaS
                </button>
                <button onClick={() => handleNav('business-phone')} className={`block w-full text-left py-2 text-sm hover:text-primary ${currentPage === 'business-phone' ? 'text-primary font-medium' : 'text-slate-600'}`} data-id="element-166">
                  Business Phone
                </button>
              </div>
            </div>

            <button onClick={() => handleNav('suppliers')} className={`block w-full text-left px-3 py-3 text-base font-medium rounded-md ${currentPage === 'suppliers' ? 'text-primary bg-primary/5' : 'text-slate-700 hover:bg-slate-50 hover:text-primary'}`} data-id="element-167">
              Suppliers
            </button>
            <button onClick={() => handleNav('contact')} className={`block w-full text-left px-3 py-3 text-base font-medium rounded-md ${currentPage === 'contact' ? 'text-primary bg-primary/5' : 'text-slate-700 hover:bg-slate-50 hover:text-primary'}`} data-id="element-168">
              Contact Us
            </button>

            <div className="mt-6 pt-6 border-t border-slate-100 px-3 flex flex-col space-y-4" data-id="element-169">
              <a href="tel:+4105255284" className="flex items-center justify-center text-slate-700 font-medium py-2" data-id="element-170">
                <PhoneIcon className="w-5 h-5 mr-2 text-primary" data-id="element-171" />
                +410-525-5284
              </a>
              <button onClick={() => handleNav('home')} className="w-full text-center bg-primary text-white px-6 py-3 rounded-full font-medium" data-id="element-172">
                Get a Quote
              </button>
            </div>
          </div>
        </div>}
    </header>;
}